import { DestroyRef, inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import {
    UnsavedChangesDecision,
    UnsavedChangesDialogComponent
} from '../../Components/dialogs/common/unsaved-changes-dialog/unsaved-changes-dialog.component';

/** Implemented by screens that can hold edits the user has not saved yet. */
export interface HasUnsavedChanges {
    hasUnsavedChanges(): boolean;
    /** Resolves true only when the changes were saved and it is safe to leave. */
    save(): Promise<boolean>;
}

/** Registers a screen for the popup and unregisters it on destroy. Call from a constructor or field initializer. */
export function registerUnsavedChanges(host: HasUnsavedChanges): void {
    const service = inject(UnsavedChangesService);
    inject(DestroyRef).onDestroy(service.register(host));
}

/**
 * Shared "unsaved changes" popup. A screen registers itself once; every place that
 * navigates away just awaits `confirmLeave()`.
 */
@Injectable({ providedIn: 'root' })
export class UnsavedChangesService {
    private readonly dialog = inject(MatDialog);
    private host: HasUnsavedChanges | null = null;
    private pending: Promise<boolean> | null = null;

    /** Returns the function that unregisters the screen. Prefer `registerUnsavedChanges`. */
    register(host: HasUnsavedChanges): () => void {
        this.host = host;
        return () => {
            if (this.host === host) {
                this.host = null;
            }
        };
    }

    hasUnsavedChanges(): boolean {
        return this.host?.hasUnsavedChanges() ?? false;
    }

    /** Resolves true when the caller may leave: nothing to save, saved, or discarded. */
    confirmLeave(): Promise<boolean> {
        // One click can trigger several leave checks; they share a single popup.
        this.pending ??= this.ask().finally(() => (this.pending = null));
        return this.pending;
    }

    private async ask(): Promise<boolean> {
        const host = this.host;
        if (!host?.hasUnsavedChanges()) {
            return true;
        }

        const decision = await firstValueFrom(
            this.dialog.open<UnsavedChangesDialogComponent, void, UnsavedChangesDecision>(
                UnsavedChangesDialogComponent, { width: '450px', disableClose: true }
            ).afterClosed()
        );

        if (decision === 'discard') {
            return true;
        }
        return decision === 'save' ? this.trySave(host) : false;
    }

    // A failed save must keep the user on the screen instead of leaving callers half-navigated.
    private async trySave(host: HasUnsavedChanges): Promise<boolean> {
        try {
            return await host.save();
        } catch (error) {
            console.error('Error while saving unsaved changes:', error);
            return false;
        }
    }
}
